import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { BaseItem, SortableListProps } from './types';

export const SortableList = <T extends BaseItem>({
  items,
  onChange,
  itemRenderer,
  renderOverlay,
}: SortableListProps<T>) => {
  const [activeId, setActiveId] = useState<number | null>(null);
  const activeItem = useMemo(() => items.find((item) => item.id === activeId), [activeId, items]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      onChange(arrayMove(items, oldIndex, newIndex));
    }

    setActiveId(null);
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext items={items}>
        <ul style={{ width: '100%' }}>{items.map((item) => itemRenderer(item))}</ul>
      </SortableContext>
      <DragOverlay>
        {activeItem && (renderOverlay ? renderOverlay(activeItem) : itemRenderer(activeItem))}
      </DragOverlay>
    </DndContext>
  );
};
